import { encode, decode } from "@msgpack/msgpack";
import { ungzip } from "pako";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

export const dateFormat = (dateString = "") => {
  if (!dateString) {
    return null; // Explicitly return null for invalid input
  }

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    console.error("Invalid date string:", dateString);
    return null; // Return null if the date is invalid
  }

  // Format date as DD-MM-YYYY
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const year = date.getFullYear();
  const formattedDate = `${year}-${month}-${day}`;

  // DateTime options
  const dateOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  };

  // Get formatted date and time (e.g., 26/12/24, 1:00:00 PM)
  const formattedDateTime = date.toLocaleString("en-GB", dateOptions);

  return { formattedDate, formattedDateTime };
};

export const generateRandomId = () => {
  const randomPart = Math.floor(Math.random() * 1000); // A 3-digit random number
  return (Date.now().toString().slice(-7) + randomPart).slice(-10);
};

export const currentDateTime = () => {
  const now = new Date(Date.now());
  const isoString = now.toISOString();

  const date = isoString.split("T")[0]; // YYYY-MM-DD
  const time = isoString.split("T")[1].split(".")[0]; // HH:MM:SS
  const dateTime = isoString; // Full ISO string
  const dateYMD = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  // Next 2 months
  // const nextTwoMonthsDate = new Date(now);
  // nextTwoMonthsDate.setMonth(now.getMonth() + 2);
  // const nextTwoMonthsYMD = `${nextTwoMonthsDate.getFullYear()}-${String(nextTwoMonthsDate.getMonth() + 1).padStart(2, "0")}-${String(nextTwoMonthsDate.getDate()).padStart(2, "0")}`;

  return {
    date,
    time,
    dateTime,
    dateYMD,
    // nextTwoMonthsYMD,
  };
};

export const toISTString = (dateTime) => {
  const date = new Date(dateTime);
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istTime = new Date(date.getTime() + istOffset);
  return istTime.toISOString().replace("Z", "+05:30");
};

export const stringToDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) return null; // handle invalid date input
  return date.toISOString().split("T")[0];
};

// export const validateNumber = (value) => {
//   if (typeof value === "number" && !isNaN(value)) {
//     return value;
//   }
//   if (typeof value === "string" && !isNaN(value.trim())) {
//     return parseFloat(value);
//   }
//   return 0;
// };

export const validateNumber = (value) => {
  const num =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? parseFloat(value.trim())
        : NaN;

  return isNaN(num) ? 0 : num;
};

export const formatDate = (isoString) => {
  if (!isoString) return 0;

  const date = new Date(isoString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12; // convert to 12-hour format
  const formattedHours = String(hours).padStart(2, "0");

  return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
};

export const decodeToken = (token) => {
  const arrayToken = token.split(".");
  const tokenPayload = JSON.parse(atob(arrayToken[1]));
  return tokenPayload;
};

export const encryptData = (payload) => {
  if (!payload) return "";

  const encoded = encode(payload); // returns Uint8Array
  return btoa(String.fromCharCode(...encoded)); // convert binary to base64 string
};

export const decryptMsgData = (base64String) => {
  try {
    if (!base64String) return null;

    const binary = atob(base64String);
    const uint8Array = new Uint8Array(
      [...binary].map((char) => char.charCodeAt(0)),
    );
    return decode(uint8Array);
  } catch (error) {
    console.log("🚀 ~ decryptData ~ error:", error);
    return base64String;
  }
};

export const decryptData = (res) => {
  try {
    const responseData = res.data || res.Data;
    // Base64 decode using atob
    const binaryString = atob(responseData);
    const compressedData = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      compressedData[i] = binaryString.charCodeAt(i);
    }

    // Decompress (assuming you use pako's ungzip)
    const decompressed = ungzip(compressedData, { to: "string" });
    return decompressed;
  } catch (error) {
    return res;
    // console.log("decryptData ~ error:", error);
  }
};

export const randomFiveDigitString = () =>
  Math.floor(10000 + Math.random() * 90000).toString();

export const formatLastTradeDate = (isoString) => {
  if (!isoString) return 0;

  const date = new Date(isoString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

export const formatDateWithSeconds = (isoString) => {
  if (!isoString) return "";

  const date = new Date(isoString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  // return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  return `${hours}:${minutes}:${seconds}`;
};

export const stringtoDDMMYYYY = (string) => {
  if (!string) return "";

  const date = new Date(string);
  if (isNaN(date)) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

export const parseToTimestamp = (value) => {
  const formats = [
    dayjs.ISO_8601, // 2025-06-12T10:33:12.4
    "DD-MMM-YYYY hh:mm A", // 12-Jun-2025 10:33 AM
    "YYYY-MM-DD HH:mm:ss", // 2025-06-12 10:33:12
    "MM/DD/YYYY hh:mm A", // 06/12/2025 10:33 AM
    "YYYY-MM-DD", // 2025-06-12
    "hh:mm A", // 10:33 AM
  ];

  const date = dayjs(value, formats, true);
  return date.isValid() ? date.valueOf() : 0;
};

export const dateTimeFormat = ({ dateStr }) => {
  if (!dateStr) return { date: "", time: "", dateTime: "" };

  const formats = [
    "DD-MM-YYYY HH:mm:ss",
    "DD-MM-YYYY",
    "MM/DD/YYYY",
    "YYYY-MM-DDTHH:mm:ssZ",
    "YYYY-MM-DDTHH:mm:ss.SSSZ",
    "YYYY-MM-DDTHH:mm:ss",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD",
    "HH:mm:ss",
  ];

  let parsed = null;

  for (const format of formats) {
    const tryParse = dayjs(dateStr, format, true);
    if (tryParse.isValid()) {
      parsed = tryParse;
      break;
    }
  }

  // Fallback to default parser if none of the formats match
  if (!parsed) {
    parsed = dayjs(dateStr);
    if (!parsed.isValid()) return { date: "", time: "", dateTime: "" };
  }

  const date = parsed.format("DD-MM-YYYY");
  const dateYMD = parsed.format("YYYY-MM-DD");
  const time = parsed.format("HH:mm:ss");
  const dateTime = `${date} ${time}`;
  const dateYMDTime = `${dateYMD}T${time}`;

  return { date, time, dateTime, dateYMD, dateYMDTime };
};

export const validateNumberWithNull = (value) => {
  const num =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? parseFloat(value.trim())
        : NaN;

  return isNaN(num) ? null : num;
};
